import React, { useCallback, useEffect, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "./elegant-tooltip.scss";

/**
 * ElegantTooltip
 * ──────────────────────────────────────────────────────────────────────────────
 * A polished, brand-aligned tooltip built to replace StandardTooltip across
 * the product. Currently scoped to the X-Ray report panel — will be rolled
 * out system-wide once validated.
 *
 * Why it's CONTROLLED (and not just OverlayTrigger with `trigger`):
 * ─────────────────────────────────────────────────────────────────
 * When the trigger element opens a modal/popup on click, the modal steals
 * focus. When the user closes the modal, focus is restored to the trigger
 * (standard Bootstrap modal behavior). With an uncontrolled OverlayTrigger
 * using `trigger={["hover","focus"]}`, that focus-return immediately re-fires
 * the focus event → tooltip pops back up → bug: tooltip stays visible after
 * popup close.
 *
 * The fix: on click of the trigger we both hide the tooltip *immediately* AND
 * set a "suppress until the cursor leaves" flag. While the flag is set, focus
 * and hover events will not show the tooltip. The flag clears on mouseLeave
 * (or after the next blur+significant delay) so future interactions work
 * normally.
 *
 * Props
 * ─────
 * content      string | ReactNode   Tooltip body text (required)
 * children     ReactNode            The trigger element (required)
 * placement    string               "top" | "bottom" | "start" | "end" | "auto"
 *                                   Default: "top"
 * maxWidth     number               Max tooltip width in px. Default: 240
 * trigger      array                Which events drive the tooltip:
 *                                   ["hover","focus"] (default), ["hover"], ["focus"]
 * delay        object               Show/hide delay ms. Default: { show: 220, hide: 80 }
 * disabled     bool                 When true, tooltip is not rendered. Default: false
 *
 * Usage
 * ─────
 * <ElegantTooltip content="This discipline is not yet complete" placement="top">
 *   <button className="some-btn">Finance</button>
 * </ElegantTooltip>
 */
export const ElegantTooltip = ({
  content,
  children,
  placement = "top",
  maxWidth = 240,
  trigger = ["hover", "focus"],
  delay = { show: 220, hide: 80 },
  disabled = false,
}) => {
  const [show, setShow] = useState(false);

  // Timers + the "just clicked" gate. Refs (not state) because we don't want
  // re-renders from these — they only gate `setShow` calls.
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  // True from onClick until the user genuinely moves their cursor off the
  // trigger AND enough time has passed that this can't be a modal-backdrop
  // mouseLeave race. While true, every show attempt (hover or focus) is
  // ignored — this is what kills the "tooltip shows after popup close" bug.
  const justClickedRef = useRef(false);
  const clickedAtRef = useRef(0);

  // Cleanup on unmount so timers don't fire after the tooltip is gone.
  useEffect(
    () => () => {
      clearTimeout(showTimerRef.current);
      clearTimeout(hideTimerRef.current);
    },
    [],
  );

  const scheduleShow = useCallback(() => {
    if (justClickedRef.current) return; // gate
    clearTimeout(hideTimerRef.current);
    clearTimeout(showTimerRef.current);
    showTimerRef.current = setTimeout(() => setShow(true), delay.show);
  }, [delay.show]);

  const scheduleHide = useCallback(() => {
    clearTimeout(showTimerRef.current);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setShow(false), delay.hide);
  }, [delay.hide]);

  const hideNow = useCallback(() => {
    clearTimeout(showTimerRef.current);
    clearTimeout(hideTimerRef.current);
    setShow(false);
  }, []);

  // Early return AFTER hooks so React's hook order stays stable across renders.
  if (!content || disabled) return children ?? null;

  const handlesHover = trigger.includes("hover");
  const handlesFocus = trigger.includes("focus");

  // Clone the child and add our event handlers on top of whatever the child
  // already had. Original handlers are preserved by chaining through
  // children.props.<handler>?.(e).
  const wrappedChild = React.cloneElement(children, {
    ...(handlesHover && {
      onMouseEnter: (e) => {
        // scheduleShow() itself gates on justClickedRef, so re-entering the
        // button after a click (e.g. backdrop disappears, cursor still over
        // the button) won't accidentally re-show the tooltip.
        scheduleShow();
        children.props.onMouseEnter?.(e);
      },
      onMouseLeave: (e) => {
        // Only treat this as a "real" mouseLeave — and therefore as the
        // signal to clear the just-clicked gate — if it happened well after
        // the click. The modal backdrop covering the button fires mouseLeave
        // within milliseconds of click; without this guard, that race would
        // clear the gate before the modal even finishes opening, and the
        // bug would come right back.
        const sinceClick = Date.now() - clickedAtRef.current;
        if (sinceClick > 250) {
          justClickedRef.current = false;
        }
        scheduleHide();
        children.props.onMouseLeave?.(e);
      },
    }),
    ...(handlesFocus && {
      onFocus: (e) => {
        scheduleShow(); // gated by justClickedRef inside
        children.props.onFocus?.(e);
      },
      onBlur: (e) => {
        // No clearing of justClickedRef on blur — Bootstrap modals restore
        // focus to the trigger on close, and that "fresh" focus arrives at
        // an unbounded time after the blur. Letting blur clear the gate
        // (even with a long delay) would re-introduce the bug for any
        // popup interaction that lasted longer than the delay.
        scheduleHide();
        children.props.onBlur?.(e);
      },
    }),
    onClick: (e) => {
      // The click is opening something (modal, popup, navigation). Tear the
      // tooltip down right now and arm the gate. The gate stays until the
      // user genuinely mouseLeaves the trigger (the only reliable "fresh
      // interaction starts here" signal).
      hideNow();
      justClickedRef.current = true;
      clickedAtRef.current = Date.now();
      children.props.onClick?.(e);
    },
  });

  const renderTooltip = (props) => (
    <Tooltip
      id="elegant-tooltip"
      className="elegant-tooltip"
      style={{ "--et-max-width": `${maxWidth}px` }}
      {...props}
    >
      <span className="elegant-tooltip__text">{content}</span>
    </Tooltip>
  );

  return (
    <OverlayTrigger
      // Controlled — react-bootstrap will respect our `show` value and skip
      // wiring its own trigger handlers.
      show={show}
      overlay={renderTooltip}
      placement={placement}
      container={typeof document !== "undefined" ? document.body : undefined}
    >
      {wrappedChild}
    </OverlayTrigger>
  );
};
